import React, { Fragment, useContext } from "react";
import { COLORS, WINDOWS_REPLAYER_URL } from "../const";
import { state } from "../state";
import { DataContext, endReasonToString } from "../types";
import "./event.scss";
const Player = ({ id, timestamp }) => {
    const data = useContext(DataContext);
    const player = data.players.find((player, i) => (player.id ?? i) === id);
    if (player == null)
        return React.createElement(React.Fragment, null, `Invalid Player ID: ${id}`);
    const isImpostor = data.impostors.includes(id);
    const isDead = player.dead_at == null ? false : player.dead_at < timestamp;
    return React.createElement("span", { "data-color": player.color, className: `player ${isImpostor ? "player-impostor" : "player-crewmate"}` },
        React.createElement("img", { className: "player-icon", src: `${WINDOWS_REPLAYER_URL}res/${isDead ? "ghost" : "player"}/${COLORS.indexOf(player.color)}.png`, alt: player.color, title: player.color }),
        React.createElement("span", { className: "player-name" }, player.name));
};
const TimestampLink = ({ timestamp }) => {
    return React.createElement("a", { href: "#", className: "timestamp-link", onClick: e => {
            e.preventDefault();
            state.absoluteSeek(timestamp - 1);
        } },
        Math.floor(timestamp / 60),
        ":",
        Math.floor(timestamp % 60).toString().padStart(2, "0"));
};
const EventContent = ({ event }) => {
    const data = useContext(DataContext);
    switch (event.type) {
        case "kill":
            return React.createElement("div", { className: "event event-kill" },
                React.createElement(TimestampLink, { timestamp: event.timestamp }),
                React.createElement(Player, { id: event.imposter, timestamp: event.timestamp }),
                " \u304C ",
                React.createElement(Player, { id: event.victim, timestamp: event.timestamp }),
                " \u3092\u30AD\u30EB");
        case "enter_vent":
            return React.createElement("div", { className: "event event-enter-vent" },
                React.createElement(TimestampLink, { timestamp: event.timestamp }),
                React.createElement(Player, { id: event.player, timestamp: event.timestamp }),
                " \u304C\u30D9\u30F3\u30C8\u5165\u308A");
        case "exit_vent":
            return React.createElement("div", { className: "event event-exit-vent" },
                React.createElement(TimestampLink, { timestamp: event.timestamp }),
                React.createElement(Player, { id: event.player, timestamp: event.timestamp }),
                " \u304C\u30D9\u30F3\u30C8\u9000\u51FA");
        case "start_meeting":
            const dead_player = event.dead_body == null ? null : data.players.find((player, i) => (player.id ?? i) === event.dead_body);
            const diff_seconds = Math.floor(dead_player == null ? 0 : event.timestamp - (dead_player.dead_at ?? 0));
            return React.createElement("div", { className: "event event-start-meeting" },
                React.createElement(TimestampLink, { timestamp: event.timestamp }),
                React.createElement(Player, { id: event.player, timestamp: event.timestamp }),
                " \u304C\u30DF\u30FC\u30C6\u30A3\u30F3\u30B0\u958B\u59CB (",
                event.dead_body == null
                    ? "エマージェンシーボタン"
                    : React.createElement(React.Fragment, null,
                        React.createElement(Player, { id: event.dead_body, timestamp: event.timestamp }),
                        "\u306E\u6B7B\u4F53\u3092\u767A\u898B\u3001\u6B7B\u4EA1\u304B\u3089",
                        diff_seconds > 60 ? `${Math.floor(diff_seconds / 60)}分${diff_seconds % 60}秒` : `${diff_seconds}秒`,
                        "\u5F8C"),
                ")");
        case "vote_finish":
            return React.createElement("div", { className: "event event-vote-finish" },
                React.createElement(TimestampLink, { timestamp: event.timestamp }),
                React.createElement("table", null,
                    React.createElement("colgroup", null,
                        React.createElement("col", { span: 1, style: { width: "50%" } }),
                        React.createElement("col", { span: 1, style: { width: "50%" } })),
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", { colSpan: 2 }, "\u6295\u7968\u7D42\u4E86\uFF01"))),
                    React.createElement("tfoot", null,
                        React.createElement("tr", null,
                            React.createElement("th", { colSpan: 2 },
                                React.createElement("strong", null, event.exiled == null ? (event.is_tie ? "スキップ (同数票のため)" : "スキップ") : React.createElement(React.Fragment, null,
                                    React.createElement(Player, { id: event.exiled, timestamp: event.timestamp }),
                                    "\u3092\u51E6\u5211"))))),
                    React.createElement("tbody", null, event.states.map((state, player_id_) => {
                        const player_id = state.id ?? player_id_;
                        const player = data.players.find((p, i) => (p.id ?? i) === player_id);
                        if (player == null)
                            return null;
                        if (player.dead_at != null && player.dead_at < event.timestamp)
                            return null;
                        return React.createElement("tr", { key: player_id },
                            React.createElement("td", null,
                                React.createElement(Player, { id: player_id, timestamp: event.timestamp })),
                            React.createElement("td", null, state.voted_for != null ? React.createElement(Player, { id: state.voted_for, timestamp: event.timestamp }) : state.did_vote ? "スキップ" : "(無投票)"));
                    }))));
        case "voted":
            return React.createElement("div", { className: "event-voted" });
        case "chat":
            return React.createElement("div", { className: "event event-chat" },
                React.createElement(TimestampLink, { timestamp: event.timestamp }),
                React.createElement(Player, { id: event.player, timestamp: event.timestamp }),
                ": ",
                event.text);
        default:
            return React.createElement("div", null, JSON.stringify(event));
    }
    return null;
};
export const Events = () => {
    const data = useContext(DataContext);
    return React.createElement("div", { className: "events" },
        React.createElement("div", { className: "event-start" },
            new Date(data.started_at * 1000).toLocaleString(),
            "\u306B\u958B\u59CB"),
        React.createElement("div", { className: "members" },
            React.createElement("p", null,
                "\u30A4\u30F3\u30DD\u30B9\u30BF\u30FC (",
                data.impostors.length,
                "):"),
            data.impostors.map(i => React.createElement(Player, { id: i, key: i, timestamp: 0 })),
            React.createElement("p", null,
                "\u30AF\u30EB\u30FC\u30E1\u30A4\u30C8 (",
                data.players.length - data.impostors.length,
                "):"),
            data.players.map((p, i) => {
                const id = p.id ?? i;
                return data.impostors.includes(id) ? null : React.createElement(Player, { id: id, key: i, timestamp: 0 });
            }),
            React.createElement("p", null,
                "\u306E\u8A08",
                data.players.length,
                "\u4EBA\u3067\u30B9\u30BF\u30FC\u30C8")),
        data.events.map((event, i) => {
            const beforeTimestamp = i === 0 ? 0 : data.events[i - 1].timestamp;
            const diff = Math.floor(event.timestamp - beforeTimestamp);
            return React.createElement(Fragment, { key: i },
                diff > 1 && React.createElement("div", { className: "event-time-margin", style: { marginTop: `${diff}px` } }),
                React.createElement(EventContent, { event: event }));
        }),
        React.createElement("div", { className: "evnet-end-game" },
            React.createElement("p", null,
                React.createElement("strong", null, endReasonToString(data.end_reason)),
                "\u3067\u30B2\u30FC\u30E0\u7D42\u4E86\uFF01"),
            React.createElement("p", null,
                "\u6240\u8981\u6642\u9593: ",
                Math.floor(data.duration / 60),
                "\u5206",
                Math.floor(data.duration % 60).toString().padStart(2, "0"),
                "\u79D2")));
};
